import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { createPortal } from 'react-dom';

import {
  acceptsPath,
  assetDisplayName,
  assetTypeName,
  draggedAssetPath,
  filterAssets,
  parseAssetValue,
  toAssetValue,
  type AssetSpec,
} from '../../../lib/asset-types.js';
import { openAsset } from '../../../lib/open-asset.js';
import { promptText } from '../../../lib/prompt-dialog.js';
import { useAssets } from '../../../state/assets.js';
import { useProjectFiles } from '../../../state/project-files.js';
import { useWorkspace } from '../../../state/workspace.js';
import { AssetThumb } from '../../panels/AssetThumb.js';
import { ContextMenu, type MenuItem } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import './asset-field.css';

/**
 * Unity-style object field for a project file reference: thumbnail + name
 * (or "None (Material)" / "Missing (Material)"), a picker popup with search
 * (⊙), drag and drop from the Assets panel, click to ping the file in Assets,
 * double-click to open it, and a clear button. Stores the path, with the
 * spec's prefix (`model:` / `gltf:` for mesh sources) when it has one.
 */
export function AssetField({
  spec,
  value,
  onChange,
}: {
  spec: AssetSpec;
  value: unknown;
  onChange: (value: string | undefined) => void;
}) {
  const projectId = useWorkspace((s) => s.projectId);
  const entries = useProjectFiles((s) => s.entries);
  const [picking, setPicking] = useState<DOMRect | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [drop, setDrop] = useState<'ok' | 'bad' | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Value when the picker opened: Escape restores it (Unity's object selector). */
  const original = useRef<string>('');

  const typeName = assetTypeName(spec);
  const parsed = parseAssetValue(spec, value);
  const entry = parsed.path ? entries.find((e) => e.kind === 'file' && e.path === parsed.path) : undefined;
  const missing = parsed.path !== null && !entry && entries.length > 0;
  const label = parsed.builtin
    ? parsed.builtin.startsWith('primitive:')
      ? `${assetDisplayName(parsed.builtin)} (built-in)`
      : parsed.builtin
    : parsed.path
      ? missing
        ? `Missing (${typeName})`
        : assetDisplayName(parsed.raw.includes('#') ? parsed.raw.slice(parsed.raw.lastIndexOf('/') + 1) : parsed.path)
      : `None (${typeName})`;

  function onDragOver(e: DragEvent) {
    const path = draggedAssetPath(e.dataTransfer);
    if (!path) return;
    e.stopPropagation();
    if (acceptsPath(spec, path)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'link';
      setDrop('ok');
    } else {
      e.dataTransfer.dropEffect = 'none';
      setDrop('bad');
    }
  }
  function onDrop(e: DragEvent) {
    const path = draggedAssetPath(e.dataTransfer);
    setDrop(null);
    if (!path || !acceptsPath(spec, path)) return;
    e.preventDefault();
    e.stopPropagation();
    onChange(toAssetValue(spec, path));
  }

  const menuItems: MenuItem[] = [
    {
      id: 'pick',
      label: `Select ${typeName}…`,
      icon: 'target',
      run: () => {
        original.current = parsed.raw;
        if (rootRef.current) setPicking(rootRef.current.getBoundingClientRect());
      },
    },
    { id: 'ping', label: 'Show in Assets', icon: 'folder', disabled: !entry, run: () => parsed.path && useAssets.getState().pingAsset(parsed.path) },
    { id: 'open', label: 'Open', icon: 'edit', disabled: !entry, run: () => parsed.path && openAsset(parsed.path) },
    { id: 'copy', label: 'Copy path', icon: 'copy', disabled: !parsed.raw, run: () => void navigator.clipboard?.writeText(parsed.raw) },
    {
      id: 'edit',
      label: 'Edit path…',
      icon: 'pencil',
      run: async () => {
        const next = await promptText(`${typeName} path`, parsed.raw);
        if (next !== null) onChange(next.trim() || undefined);
      },
    },
    { id: 'sep', label: '', separator: true },
    { id: 'clear', label: 'Clear', icon: 'x', disabled: !parsed.raw, run: () => onChange(undefined) },
  ];

  return (
    <div
      ref={rootRef}
      className={`asset-field${parsed.raw ? '' : ' is-empty'}${missing ? ' is-missing' : ''}${drop ? ` is-drop-${drop}` : ''}`}
      onDragOver={onDragOver}
      onDragLeave={() => setDrop(null)}
      onDrop={onDrop}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      <button
        type="button"
        className="asset-field-body"
        title={parsed.raw ? `${parsed.raw}\nClick: show in Assets · Double-click: open` : `Drop a ${typeName.toLowerCase()} here or pick one`}
        onClick={() => parsed.path && entry && useAssets.getState().pingAsset(parsed.path)}
        onDoubleClick={() => parsed.path && entry && openAsset(parsed.path)}
      >
        {projectId && parsed.path && entry ? (
          <AssetThumb projectId={projectId} path={parsed.path} version={entry.modifiedAt} size={18} className="asset-field-thumb" />
        ) : (
          <span className="asset-field-thumb asset-field-glyph">
            <Icon name={missing ? 'warning' : parsed.builtin ? 'cube' : 'target'} size={12} />
          </span>
        )}
        <span className="asset-field-name">{label}</span>
      </button>
      {parsed.raw && (
        <button type="button" className="asset-field-btn" title="Clear" aria-label="Clear" onClick={() => onChange(undefined)}>
          <Icon name="x" size={11} />
        </button>
      )}
      <button
        type="button"
        className="asset-field-btn"
        title={`Select ${typeName}`}
        aria-label={`Select ${typeName}`}
        aria-haspopup="dialog"
        onClick={() => {
          original.current = parsed.raw;
          setPicking(picking ? null : rootRef.current!.getBoundingClientRect());
        }}
      >
        <Icon name="target" size={12} />
      </button>
      {picking && (
        <AssetPicker
          spec={spec}
          anchor={picking}
          current={parsed.builtin ?? parsed.path}
          onPick={(v) => {
            if ((v ?? '') !== parsed.raw) onChange(v);
          }}
          onCancel={() => {
            if (original.current !== parsed.raw) onChange(original.current || undefined);
          }}
          onClose={() => setPicking(null)}
        />
      )}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />}
    </div>
  );
}

const PICKER_KEY = 'prism.assetPicker.view';

function readPickerView(): { grid: boolean; size: number } {
  try {
    const v = JSON.parse(localStorage.getItem(PICKER_KEY) ?? '{}') as { grid?: unknown; size?: unknown };
    return { grid: v.grid === true, size: typeof v.size === 'number' ? Math.min(128, Math.max(40, v.size)) : 64 };
  } catch {
    return { grid: false, size: 64 };
  }
}

/**
 * Unity's object selector: search the project's files of the field's type
 * (plus None and built-ins). Like Unity, choosing previews live (click or
 * arrow keys assign the value), Enter / double-click closes, and Escape
 * restores the value the field had when the picker opened.
 */
export function AssetPicker({
  spec,
  anchor,
  current,
  onPick,
  onCancel,
  onClose,
}: {
  spec: AssetSpec;
  anchor: DOMRect;
  /** Selected file path or builtin. */
  current: string | null;
  /** Assigns a value (live preview; the picker stays open). */
  onPick: (value: string | undefined) => void;
  /** Escape: restore the value from before the picker opened. */
  onCancel?: () => void;
  onClose: () => void;
}) {
  const projectId = useWorkspace((s) => s.projectId);
  const entries = useProjectFiles((s) => s.entries);
  const [query, setQuery] = useState('');
  const [view, setViewState] = useState(readPickerView);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: anchor.left, top: anchor.bottom + 4 });

  const setView = (next: { grid: boolean; size: number }) => {
    setViewState(next);
    try {
      localStorage.setItem(PICKER_KEY, JSON.stringify(next));
    } catch {
      // Not remembered.
    }
  };

  const versions = useMemo(() => new Map(entries.map((e) => [e.path, e.modifiedAt])), [entries]);
  const files = useMemo(
    () => entries.filter((e) => e.kind === 'file' && acceptsPath(spec, e.path)).map((e) => e.path),
    [entries, spec],
  );
  const builtins = useMemo(() => {
    const words = query.trim().toLowerCase();
    return (spec.builtins ?? []).filter((b) => !words || b.toLowerCase().includes(words));
  }, [spec, query]);
  const items: (string | null)[] = useMemo(
    () => [...(query.trim() ? [] : [null]), ...builtins, ...filterAssets(files, query)],
    [builtins, files, query],
  );

  useEffect(() => {
    const i = items.indexOf(current);
    setActive(i >= 0 ? i : 0);
    // Only when the list changes shape, not on every keystroke's highlight.
  }, [items, current]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = anchor.bottom + 4;
    const top = below + r.height > window.innerHeight - 6 ? Math.max(6, anchor.top - r.height - 4) : below;
    setPos({ left: Math.max(6, Math.min(anchor.left, window.innerWidth - r.width - 6)), top });
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  useEffect(() => {
    ref.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const preview = (item: string | null | undefined) => {
    if (item === undefined) return;
    onPick(item === null ? undefined : spec.builtins?.includes(item) ? item : toAssetValue(spec, item));
  };
  const pick = (item: string | null | undefined) => {
    preview(item);
    onClose();
  };
  const move = (to: number) => {
    const next = Math.max(0, Math.min(items.length - 1, to));
    setActive(next);
    preview(items[next]);
  };

  const typeName = assetTypeName(spec);
  const columns = view.grid ? Math.max(1, Math.floor(316 / (view.size + 8))) : 1;

  return createPortal(
    <div
      ref={ref}
      className="asset-picker"
      role="dialog"
      aria-label={`Select ${typeName}`}
      style={pos}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onCancel?.();
          onClose();
        } else if (e.key === 'ArrowDown') move(active + columns);
        else if (e.key === 'ArrowUp') move(active - columns);
        else if (view.grid && e.key === 'ArrowRight') move(active + 1);
        else if (view.grid && e.key === 'ArrowLeft') move(active - 1);
        else if (e.key === 'Enter') pick(items[active]);
        else return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <header className="asset-picker-head">
        <strong>Select {typeName}</strong>
        <span className="muted">{files.length}</span>
        <span className="asset-picker-spacer" />
        <button
          type="button"
          className={`ibtn${view.grid ? '' : ' is-active'}`}
          title="List"
          aria-pressed={!view.grid}
          onClick={() => setView({ ...view, grid: false })}
        >
          <Icon name="list" size={13} />
        </button>
        <button
          type="button"
          className={`ibtn${view.grid ? ' is-active' : ''}`}
          title="Grid"
          aria-pressed={view.grid}
          onClick={() => setView({ ...view, grid: true })}
        >
          <Icon name="grid" size={13} />
        </button>
        {view.grid && (
          <input
            type="range"
            className="asset-size-slider"
            min={40}
            max={128}
            value={view.size}
            aria-label="Preview size"
            onChange={(e) => setView({ ...view, size: Number(e.target.value) })}
          />
        )}
      </header>
      <div className="search-input compact asset-picker-search">
        <Icon name="search" size={12} />
        <input
          autoFocus
          value={query}
          placeholder={`Search ${typeName.toLowerCase()}s`}
          aria-label="Search"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div
        className={`asset-picker-list${view.grid ? ' is-grid' : ''}`}
        role="listbox"
        style={view.grid ? { gridTemplateColumns: `repeat(auto-fill, minmax(${view.size + 8}px, 1fr))` } : undefined}
      >
        {items.map((item, i) => {
          const isBuiltin = item !== null && (spec.builtins?.includes(item) ?? false);
          const size = view.grid ? view.size : 22;
          return (
            <button
              key={item ?? '__none'}
              type="button"
              role="option"
              data-index={i}
              aria-selected={item === current}
              className={`asset-picker-item${i === active ? ' is-active' : ''}${item === current ? ' is-current' : ''}`}
              title={item ?? 'None'}
              onClick={() => {
                setActive(i);
                preview(item);
              }}
              onDoubleClick={() => pick(item)}
            >
              {item === null || isBuiltin || !projectId ? (
                <span className="asset-icon asset-thumb" style={{ width: size, height: size }}>
                  <Icon name={item === null ? 'x' : 'cube'} size={Math.max(12, Math.round(size * 0.45))} />
                </span>
              ) : (
                <AssetThumb projectId={projectId} path={item} version={versions.get(item) ?? ''} size={size} />
              )}
              <span className="asset-picker-name">{item === null ? 'None' : assetDisplayName(item)}</span>
              {!view.grid && item !== null && !isBuiltin && (
                <span className="asset-picker-dir">{item.includes('/') ? item.slice(0, item.lastIndexOf('/')) : ''}</span>
              )}
            </button>
          );
        })}
        {items.length === 0 && <p className="muted asset-picker-empty">No {typeName.toLowerCase()} files match.</p>}
      </div>
      <footer className="asset-picker-foot">Click to preview · Enter / double-click to choose · Esc to revert</footer>
    </div>,
    document.body,
  );
}
